import { Injectable } from "@angular/core";

type UserProps = {
	user_oid?: string;
	tid?: string;
	upn?: string;
};

type GAWindow = Window &
	typeof globalThis & {
		dataLayer?: any[];
		gtag?: (...args: any[]) => void;
	};

@Injectable({ providedIn: "root" })
export class AnalyticsService {
	private initialized = false;
	private gaId?: string;
	private apiSecret?: string;
	private lastUserProps?: UserProps;
	private debugMode = false;
	private fallbackClientId?: string;
	private engagementStart?: number;
	private lastPagePath?: string;
	private lastPageLocation?: string;
	private lastPageTitle?: string;
	private engagementTrackingInitialized = false;
	private engagementHeartbeatId?: number;
	private readonly engagementHeartbeatMs = 10000;
	private readonly mpEngagementEventName = "engagement_heartbeat";
	private readonly sessionTimeoutMs = 30 * 60 * 1000;

	private readonly handleVisibilityChange = () => {
		if (typeof document === "undefined") {
			return;
		}
		if (document.visibilityState === "hidden") {
			this.flushEngagement();
			return;
		}
		if (document.visibilityState === "visible") {
			this.engagementStart = Date.now();
		}
	};

	private readonly handleBeforeUnload = () => {
		this.flushEngagement();
	};

	init(gaId?: string, apiSecret?: string, debugMode = false): void {
		if (!gaId || this.initialized || typeof window === "undefined") {
			return;
		}

		this.gaId = gaId;
		this.apiSecret = apiSecret?.trim() || undefined;
		this.debugMode =
			debugMode ||
			/localhost|127\.0\.0\.1/i.test(window.location.hostname);
		this.injectScript(gaId);
		const win = window as GAWindow;
		win.dataLayer = win.dataLayer || [];
		win.gtag = (...args: any[]) => win.dataLayer!.push(args);

		win.gtag("js", new Date());
		win.gtag("config", gaId, {
			send_page_view: false,
			debug_mode: this.debugMode,
		});
		this.initialized = true;
		this.ensureFallbackClientId();
		this.startEngagementTracking();
	}

	setUserProperties(props: UserProps): void {
		if (!this.isReady()) {
			return;
		}
		const { user_oid, tid, upn } = props;
		if (!user_oid && !tid && !upn) {
			return;
		}

		const snapshot = JSON.stringify({ user_oid, tid, upn });
		if (snapshot === JSON.stringify(this.lastUserProps)) {
			return;
		}

		const win = window as GAWindow;
		win.gtag?.("set", "user_properties", { user_oid, tid, upn });
		win.gtag?.("event", "user_id_set", {
			user_oid,
			tid,
			upn,
			debug_mode: this.debugMode,
		});
		this.lastUserProps = { user_oid, tid, upn };

		// Measurement Protocol
		this.sendMeasurementProtocolEvent("user_id_set", {
			user_oid,
			tid,
			upn,
		});
	}

	trackPageView(path: string): void {
		if (!this.isReady()) {
			return;
		}
		this.flushEngagement();
		this.captureCurrentPage(path);
		if (typeof document === "undefined" || document.visibilityState !== "hidden") {
			this.engagementStart = Date.now();
		}
		const win = window as GAWindow;
		const pageLocation = this.lastPageLocation;
		const pageTitle = this.lastPageTitle;
		win.gtag?.("event", "page_view", {
			page_path: path,
			page_location: pageLocation,
			page_title: pageTitle,
			debug_mode: this.debugMode,
		});

		// Measurement Protocol 
		this.sendMeasurementProtocolEvent("page_view", {
			page_path: path,
			page_location: pageLocation,
			page_title: pageTitle,
		});
	}

	private injectScript(gaId: string): void {
		const exists = document.querySelector(
			`script[src^="https://www.googletagmanager.com/gtag/js?id=${gaId}"]`
		);
		if (exists) {
			return;
		}

		const script = document.createElement("script");
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
		document.head.appendChild(script);
	}

	private isReady(): boolean {
		return this.initialized && !!this.gaId && typeof window !== "undefined";
	}

	private ensureFallbackClientId(): void {
		if (typeof window === "undefined") {
			return;
		}
		const storageKey = "ga_fallback_cid";
		const existing = window.localStorage.getItem(storageKey);
		if (existing) {
			this.fallbackClientId = existing;
			return;
		}
		const cid = this.generateCid();
		this.fallbackClientId = cid;
		window.localStorage.setItem(storageKey, cid);
	}

	private generateCid(): string {
		const w = window as any;
		if (w.crypto && typeof w.crypto.randomUUID === "function") {
			return w.crypto.randomUUID();
		}
		return "cid-" + Math.random().toString(16).slice(2) + Date.now().toString(16);
	}

	private sendMeasurementProtocolEvent(
		eventName: string,
		params: Record<string, string | number | undefined>,
		engagementTimeMsec?: number,
		options?: { skipSessionStart?: boolean }
	): void {
		if (!this.gaId || !this.apiSecret || typeof fetch === "undefined") {
			return;
		}
		if (!this.fallbackClientId) {
			this.ensureFallbackClientId();
		}
		const sessionParams = this.getSessionParams();
		const payloadParams = sessionParams
			? {
					session_id: sessionParams.session_id,
					session_number: sessionParams.session_number,
					...params,
				}
			: params;
		const cid = this.fallbackClientId || "cid-missing";
		const pageLocation =
			typeof window !== "undefined" ? window.location.href : undefined;
		const resolvedEngagementTime =
			typeof engagementTimeMsec === "number"
				? Math.max(Math.round(engagementTimeMsec), 1)
				: 1;
		if (
			sessionParams?.is_new_session &&
			!options?.skipSessionStart &&
			eventName !== "session_start"
		) {
			const sessionStartParams = {
				page_location: payloadParams.page_location || pageLocation,
				page_title: payloadParams.page_title,
				page_path: payloadParams.page_path,
			};
			this.sendMeasurementProtocolEvent("session_start", sessionStartParams, 1, {
				skipSessionStart: true,
			});
			if (sessionParams.session_number === 1) {
				this.sendMeasurementProtocolEvent("first_visit", sessionStartParams, 1, {
					skipSessionStart: true,
				});
			}
		}
		const payload: any = {
			client_id: cid,
			events: [
				{
					name: eventName,
					params: {
						...payloadParams,
						...(payloadParams.page_location
							? {}
							: pageLocation
							? { page_location: pageLocation }
							: {}),
						engagement_time_msec: resolvedEngagementTime,
						...(this.debugMode ? { debug_mode: 1 } : {}),
					},
				},
			],
		};
		const userOid = params.user_oid || this.lastUserProps?.user_oid;
		const tid = params.tid || this.lastUserProps?.tid;
		const upn = params.upn || this.lastUserProps?.upn;
		if (userOid) {
			payload.user_id = userOid;
		}
		const userProps: Record<string, any> = {};
		if (userOid) userProps.user_oid = { value: userOid };
		if (tid) userProps.tid = { value: tid };
		if (upn) userProps.upn = { value: upn };
		if (Object.keys(userProps).length) {
			payload.user_properties = userProps;
		}

		const url = `https://www.google-analytics.com/mp/collect?measurement_id=${encodeURIComponent(
			this.gaId
		)}&api_secret=${encodeURIComponent(this.apiSecret)}`;

		fetch(url, {
			method: "POST",
			mode: "no-cors",
			keepalive: true,
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		}).catch(() => {});
	}

	private getSessionParams():
		| {
				session_id: number;
				session_number: number;
				is_new_session: boolean;
		  }
		| null {
		if (typeof window === "undefined") {
			return null;
		}
		const storage = window.localStorage;
		const now = Date.now();
		const sessionIdKey = "ga_fallback_sid";
		const sessionNumberKey = "ga_fallback_snum";
		const lastActivityKey = "ga_fallback_slast";
		const storedSessionId = Number(storage.getItem(sessionIdKey));
		const storedSessionNumber = Number(storage.getItem(sessionNumberKey));
		const lastActivity = Number(storage.getItem(lastActivityKey));
		const hasSession =
			Number.isFinite(storedSessionId) &&
			storedSessionId > 0 &&
			Number.isFinite(storedSessionNumber) &&
			storedSessionNumber > 0 &&
			Number.isFinite(lastActivity) &&
			lastActivity > 0;
		const isExpired =
			!hasSession || now - lastActivity > this.sessionTimeoutMs;

		if (isExpired) {
			const sessionId = Math.floor(now / 1000);
			const sessionNumber =
				Number.isFinite(storedSessionNumber) && storedSessionNumber > 0
					? storedSessionNumber + 1
					: 1;
			storage.setItem(sessionIdKey, String(sessionId));
			storage.setItem(sessionNumberKey, String(sessionNumber));
			storage.setItem(lastActivityKey, String(now));
			return {
				session_id: sessionId,
				session_number: sessionNumber,
				is_new_session: true,
			};
		}

		storage.setItem(lastActivityKey, String(now));
		return {
			session_id: storedSessionId,
			session_number: storedSessionNumber,
			is_new_session: false,
		};
	}

	private startEngagementTracking(): void {
		if (this.engagementTrackingInitialized || typeof window === "undefined") {
			return;
		}
		this.engagementTrackingInitialized = true;
		this.captureCurrentPage();
		if (typeof document === "undefined" || document.visibilityState !== "hidden") {
			this.engagementStart = Date.now();
		}
		if (typeof document !== "undefined") {
			document.addEventListener("visibilitychange", this.handleVisibilityChange);
		}
		window.addEventListener("beforeunload", this.handleBeforeUnload);
		window.addEventListener("pagehide", this.handleBeforeUnload);
		this.startEngagementHeartbeat();
	}

	private startEngagementHeartbeat(): void {
		if (this.engagementHeartbeatId || typeof window === "undefined") {
			return;
		}
		this.engagementHeartbeatId = window.setInterval(() => {
			if (!this.isReady()) {
				return;
			}
			if (typeof document !== "undefined" && document.visibilityState === "hidden") {
				return;
			}
			this.flushEngagement(true);
		}, this.engagementHeartbeatMs);
	}

	private captureCurrentPage(path?: string): void {
		if (typeof window === "undefined") {
			return;
		}
		this.lastPagePath =
			path ??
			window.location.pathname +
				window.location.search +
				window.location.hash;
		this.lastPageLocation = window.location.href;
		this.lastPageTitle =
			typeof document !== "undefined" ? document.title : undefined;
	}

	private flushEngagement(restart = false): void {
		if (!this.isReady() || !this.engagementStart) {
			return;
		}
		const elapsed = Date.now() - this.engagementStart;
		if (elapsed <= 0) {
			return;
		}
		this.engagementStart = undefined;
		const engagementTimeMsec = Math.max(Math.round(elapsed), 1);
		const pageLocation =
			this.lastPageLocation ||
			(typeof window !== "undefined" ? window.location.href : undefined);
		const pageTitle =
			this.lastPageTitle ||
			(typeof document !== "undefined" ? document.title : undefined);
		const pagePath =
			this.lastPagePath ||
			(typeof window !== "undefined"
				? window.location.pathname +
					window.location.search +
					window.location.hash
				: undefined);

		const win = window as GAWindow;
		win.gtag?.("event", "user_engagement", {
			engagement_time_msec: engagementTimeMsec,
			page_location: pageLocation,
			page_title: pageTitle,
			page_path: pagePath,
			debug_mode: this.debugMode,
		});

		// Measurement Protocol send (use a custom event to carry engagement time)
		this.sendMeasurementProtocolEvent(
			this.mpEngagementEventName,
			{
				page_location: pageLocation,
				page_title: pageTitle,
				page_path: pagePath,
			},
			engagementTimeMsec
		);

		if (
			restart &&
			(typeof document === "undefined" || document.visibilityState !== "hidden")
		) {
			this.engagementStart = Date.now();
		}
	}
}
